import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { rewards as rewardsApi } from '@/api/endpoints'

import { Alert, Modal } from '@/components/ui'
import { formatMoney, getActiveLocale, parseNaive } from '@/lib/utils'
import type { RewardLessonQuote } from '@/types/api'

import { BonusToggles, SectionTitle } from './LessonRewardModal'
import { rewardTypeLabel } from '@/features/rewards/typeLabel'

interface Props {
  quote: RewardLessonQuote
  /** Blok, do ktorého lekcia pôjde - jeho odmena za celý deň (auto, odvoz). */
  instructorReservationId?: number | null
  /** Zapnuté vlastné bonusy - uložia sa spolu s rezerváciou. */
  bonusIds?: number[]
  onToggleBonus?: (id: number) => void
  onClose: () => void
}

/**
 * Odmena pre inštruktora za rezerváciu, ktorá ešte nie je uložená - len na
 * čítanie. Počíta ju server tými istými kalkulačkami ako pri uložení
 * (`POST /rewards/quote-lessons/`); riadky vzniknú až uložením a potom sa
 * namiesto tohto okna otvorí `LessonRewardModal`.
 */
export default function RewardQuoteModal({
  quote,
  instructorReservationId,
  bonusIds = [],
  onToggleBonus,
  onClose,
}: Props) {
  const { t } = useTranslation()
  // Odmena za celý deň už existuje pri uloženom bloku - nová lekcia ju nemení.
  const { data: blockData } = useQuery({
    queryKey: ['rewards', 'block', instructorReservationId],
    queryFn: () => rewardsApi.list({ instructor_reservation_id: instructorReservationId, limit: 50 }),
    enabled: Boolean(instructorReservationId),
  })
  const dayRows = (blockData?.results ?? []).filter((row) => row.client_reservation_id == null)
  const instructor = quote.rows.find((row) => row.instructor)?.instructor

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={instructor ? `${t('reservation.rewards')} — ${instructor}` : t('reservation.rewards')}
      footer={
        <button type="button" className="btn-primary" onClick={onClose}>
          {t('global.close')}
        </button>
      }
    >
      <div className="space-y-2">
        {quote.without_instructor ? (
          <Alert variant="warning">{t('rewards.withoutInstructor', { count: quote.without_instructor })}</Alert>
        ) : null}

        <SectionTitle title={t('rewards.forReservation')}>{t('rewards.quoteInfo')}</SectionTitle>
        {quote.rows.length === 0 ? (
          <Alert variant="info">{t('rewards.noneQuote')}</Alert>
        ) : (
          <table className="table-base table-fixed [&_tbody_td]:py-1 [&_thead_th]:py-1">
            <colgroup>
              <col className="w-28" />
              <col />
              <col className="w-24" />
              <col className="w-32" />
            </colgroup>
            <thead>
              <tr>
                <th>{t('reservation.date')}</th>
                <th>{t('rewards.type')}</th>
                <th className="text-right">{t('rewards.units')}</th>
                <th className="text-right">{t('rewards.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {quote.rows.map((row, index) => (
                <tr key={`${row.start}-${row.type_id}-${index}`}>
                  <td className="text-slate-600">{shortDate(row.start)}</td>
                  <td>{rewardTypeLabel(t, row)}</td>
                  <td className="text-right text-slate-500">
                    {row.number && !(row.type_name === 'custom-bonus' && row.number === 1)
                      ? t('rewards.minutes', { count: row.number })
                      : '—'}
                  </td>
                  <td className="text-right">{formatMoney(row.reward)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-300 font-semibold">
                <td colSpan={3} className="px-2.5 py-1 text-right">
                  {t('global.total')}
                </td>
                <td className="px-2.5 py-1 text-right">{formatMoney(quote.total)}</td>
              </tr>
            </tfoot>
          </table>
        )}

        {onToggleBonus ? <BonusToggles activeIds={bonusIds} onToggle={onToggleBonus} /> : null}

        {dayRows.length ? (
          <div className="border-t border-slate-200 pt-2">
            <SectionTitle title={t('rewards.wholeDay')}>{t('rewards.wholeDayHint')}</SectionTitle>
            <table className="table-base table-fixed [&_tbody_td]:py-1 [&_thead_th]:py-1">
            <colgroup>
              <col className="w-28" />
              <col />
              <col className="w-24" />
              <col className="w-32" />
            </colgroup>
              <tbody>
                {dayRows.map((row) => (
                  <tr key={row.id}>
                    <td />
                    <td>{rewardTypeLabel(t, row)}</td>
                    <td className="text-right text-slate-500">
                      {row.type_name === 'transporte-people' ? t('rewards.persons', { count: row.number ?? 0 }) : '—'}
                    </td>
                    <td className="text-right">{formatMoney(row.reward)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

function shortDate(value: string | null) {
  const d = parseNaive(value)
  return d ? d.toLocaleDateString(getActiveLocale(), { day: 'numeric', month: 'numeric', year: '2-digit' }) : '—'
}
